import React, { useState } from "react";
import DataTable from "@/components/Datatable/Datatable";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import MemberReportFilters from "../../../MemberReports/components/MemberReportFilters/MemberReportFilters";
import {
  MemberReportExportParams,
  MemberReportFilters as MemberReportFilterValues,
  MemberReportPageParams,
  useMemberReport,
} from "../../../MemberReports/hooks/useMemberReport";
import { useDownloadMemberReportCsv } from "../../../MemberReports/hooks/useDownloadMemberReportCsv";
import { defaultDateRange } from "../../../MemberReports/utils/defaultDateRange";
import { fetchMyProductsHistory } from "../../api/fetchMyProductsHistory/fetchMyProductsHistoryApi";
import { fetchMyProductsHistoryCsv } from "../../api/fetchMyProductsHistory/fetchMyProductsHistoryCsvApi";
import {
  MyProductsHistoryItem,
  MyProductsHistorySortKey,
} from "../../api/fetchMyProductsHistory/types";
import { myProductsHistoryColumns } from "../MyProductsHistoryColumns/MyProductsHistoryColumns";

const columns = myProductsHistoryColumns();

const SORT_KEYS: readonly MyProductsHistorySortKey[] = [
  "categoryName",
  "productName",
  "quantity",
  "orderDate",
  "unitPrice",
  "lineTotal",
  "status",
];

// The shared report state keeps a generic date range; this endpoint names
// its range after the order date.
function toApiParams<T extends MemberReportFilterValues>({
  dateFrom,
  dateTo,
  ...rest
}: T) {
  return { ...rest, orderDateFrom: dateFrom, orderDateTo: dateTo };
}

const fetchPage = (
  params: MemberReportPageParams<MyProductsHistorySortKey>,
  signal: AbortSignal,
) => fetchMyProductsHistory(toApiParams(params), signal);

const fetchCsv = (params: MemberReportExportParams<MyProductsHistorySortKey>) =>
  fetchMyProductsHistoryCsv(toApiParams(params));

/**
 * Member-facing product history report. Authenticated only: the backend
 * scopes the rows to the active company, so there is no permission and no
 * guard here.
 */
const MyProductsHistoryPage: React.FC = () => {
  const [defaultFilters] = useState(defaultDateRange);

  const {
    items,
    totalCount,
    loading,
    pagination,
    sorting,
    filters,
    exportParams,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  } = useMemberReport<MyProductsHistoryItem, MyProductsHistorySortKey>({
    fetchPage,
    sortKeys: SORT_KEYS,
    initialFilters: defaultFilters,
  });

  const { loading: csvLoading, downloadCsv } = useDownloadMemberReportCsv({
    fetchCsv,
    fileNamePrefix: "my-products-history-report",
  });

  return (
    <MainPageWrapper
      subtitle="Your company's product store purchases"
      title="Product History"
    >
      <MemberReportFilters
        dateLabel="Order Date"
        defaultFilters={defaultFilters}
        filters={filters}
        isExporting={csvLoading}
        onExportCsv={() => downloadCsv(exportParams)}
        onFilterChange={handleFilterChange}
      />
      <DataTable<MyProductsHistoryItem>
        columns={columns}
        data={items}
        loading={loading}
        noDataMessage="No product purchases found"
        onPageChange={(newPageIndex, newPageSize) =>
          handlePaginationChange({
            pageIndex: newPageIndex,
            pageSize: newPageSize,
          })
        }
        onSortingChange={handleSortingChange}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        rowKeyExtractor={(_row, index) => `${pagination.pageIndex}-${index}`}
        sorting={sorting}
        totalCount={totalCount}
      />
    </MainPageWrapper>
  );
};

export default MyProductsHistoryPage;
